import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Joi from 'joi';

import { useUpdatePasswordMutation } from 'api/main';
import { userSchemas } from 'schemas';
import { validateSchema } from 'utils/helpers';
import { PASS_REGEX } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { InfoButton, PasswordField, SubmitButton, SuccessMessage } from 'components';
import makeStyles from '@mui/styles/makeStyles';
import { Box } from '@mui/material';

const useStyles = makeStyles(theme => ({
    field: {
        width: '90%',
    },
    box: {
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '50%',
        [theme.breakpoints.down('lg')]: {
            width: '65%',
        },
        [theme.breakpoints.down('md')]: {
            width: '75%',
        },
    },
}));

const InvestorPasswordForm = () => {
    const classes = useStyles();

    const [updatePassword, { isLoading: updatePasswordLoader, isSuccess, isError, error }] =
        useUpdatePasswordMutation();

    const [updatePasswordSuccess, setUpdatePasswordSuccess] = useState(false);

    const [passwordData, setPasswordData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmNewPassword: '',
    });

    const [errors, setErrors] = useState({
        oldPassword: null,
        newPassword: null,
        confirmNewPassword: null,
    });

    const passwordSchema = useMemo(
        () => ({
            oldPassword: Joi.string()
                .required()
                .messages({ 'string.empty': texts.CURRENT_PASSWORD_INCORRECT }),
            newPassword: Joi.string().pattern(PASS_REGEX).required().messages({
                'string.empty': texts.EMPTY_NEW_PASSWORD,
                'string.pattern.base': texts.LE_MOT_DE_PASSE_DOIT_CONTENIR,
            }),
            confirmNewPassword: Joi.when('newPassword', {
                is: '',
                then: Joi.any(),
                otherwise: Joi.ref('newPassword'),
            }).messages({ 'any.only': texts.PASSWORDS_MUST_MATCH }),
        }),
        []
    );

    const validatePasswordChange = useCallback(() => {
        let { valid, newErrors } = validateSchema(passwordSchema, passwordData);
        setErrors(newErrors);
        return valid;
    }, [passwordData, passwordSchema]);

    const handleChangePassword = useCallback(e => {
        const name = e.target.name;
        const value = e.target.value;
        setPasswordData(prevState => ({ ...prevState, [name]: value }));
        setUpdatePasswordSuccess(false);
    }, []);

    const handleSubmitPasswordChange = useCallback(() => {
        if (validatePasswordChange()) {
            updatePassword(userSchemas.updatePassword(passwordData));
        }
    }, [passwordData, validatePasswordChange, updatePassword]);

    useEffect(() => {
        if (isSuccess) {
            setUpdatePasswordSuccess(true);
            setPasswordData({
                oldPassword: '',
                newPassword: '',
                confirmNewPassword: '',
            });
        } else if (isError) {
            if (error?.status === 401) {
                setErrors({ oldPassword: texts.CURRENT_PASSWORD_INCORRECT });
            } else {
                error?.data?.violations?.forEach(v => {
                    if (v.propertyPath === 'newPassword') {
                        setErrors({ newPassword: v.message });
                    }
                });
            }
        }
    }, [isSuccess, isError, error]);

    return (
        <React.Fragment>
            <Box className={classes.box}>
                <PasswordField
                    fullWidth
                    name="oldPassword"
                    className={classes.field}
                    label={texts.CURRENT_PASSWORD}
                    value={passwordData.oldPassword}
                    onChange={handleChangePassword}
                    error={errors.oldPassword}
                />
                <PasswordField
                    fullWidth
                    name="newPassword"
                    className={classes.field}
                    label={texts.NOUVEAU_MOT_DE_PASSE}
                    value={passwordData.newPassword}
                    onChange={handleChangePassword}
                    error={errors.newPassword}
                />
                <PasswordField
                    fullWidth
                    infoButton={
                        <InfoButton
                            disableMarginBottom
                            title={texts.MOT_DE_PASSE}
                            content={texts.LE_MOT_DE_PASSE_DOIT_CONTENIR}
                        />
                    }
                    name="confirmNewPassword"
                    className={classes.field}
                    label={texts.CONFIRM_NEW_PASSWORD}
                    value={passwordData.confirmNewPassword}
                    onChange={handleChangePassword}
                    error={errors.confirmNewPassword}
                />
                {updatePasswordSuccess && (
                    <SuccessMessage align="left" message={texts.MOT_DE_PASSE_A_ETE_CHANGE} />
                )}
            </Box>
            <SubmitButton
                text={texts.CHANGER_LE_MOT_DU_PASSE}
                onClick={handleSubmitPasswordChange}
                disabled={updatePasswordLoader}
                loading={updatePasswordLoader}
            />
        </React.Fragment>
    );
};

export default InvestorPasswordForm;
